import { Skeleton, styled, Typography } from '@mui/material'
import { useMemo } from 'react'
import { Room } from '@weblite/ts-action/room'
import { HallAvatar, UserAvatar } from '$common/avatar'
import { getUserDisplayName, useUser } from '$logic/users'
import { HIDE_SCROLLBAR_CLASS } from '$setup/style/constants'
import { useClientUser } from '$logic/client-user'
import { useStoryRibbons } from '$logic/story/react-hooks'
import { useHall } from '$logic/hall/react-hook'
import { OpenStorySource } from '$logic/story/analytics'
import { t } from '$i18n'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { IS_STORY_ENABLED } from '$func/environmentInfo'
import { useRooms } from '$logic/room'
import { isCallStarted, useCallStorage } from '$logic/call'
import { sortByDate } from '$func/sort'
import {
  STORY_RIBBON_HEIGHT_CSS_VAR,
  STORY_RIBBON_TRANSITION_MS,
} from './constants'

const isHall = (room: Room) => room.type === 'CHANNEL' || room.type === 'GROUP'

export function StoryRibbons() {
  const { nonClientRibbons, clientUserRibbon: clientUserStory } =
    useStoryRibbons()

  const rooms = useRooms()
  const ongoingCall = useCallStorage()

  const roomsWithCall = useMemo(() => {
    return sortByDate(
      Object.values(rooms).filter(
        room =>
          room.call &&
          isHall(room) &&
          isCallStarted(room.call) &&
          !(ongoingCall.roomId === room.id),
      ),
      room => room.call!.startedAt!,
      'descending',
    )
  }, [rooms, ongoingCall])

  const { id: clientUserId } = useClientUser()

  const clientUserHasStory = !!clientUserStory

  if (!IS_STORY_ENABLED) return null
  return (
    <StoryRibbonRoot className={HIDE_SCROLLBAR_CLASS}>
      <StoryRibbonItemWrapper>
        <UserAvatar
          userId={clientUserId}
          size={AvatarSize.LG}
          canClientUserCreateStory
          withStory
          source={OpenStorySource.STORY_RIBBON}
          isStoryRibbon
          dataTestId="story-ribbon-client-user-avatar"
        />
        <AvatarName
          name={clientUserHasStory ? t('story.yourStory') : t('story.addStory')}
        />
      </StoryRibbonItemWrapper>

      {roomsWithCall.map(room => {
        return (
          <StoryRibbonItemWrapper key={`call-${room.id + room.type}`}>
            <HallAvatar
              roomId={room.id}
              size={AvatarSize.LG}
              ongoingCallType={room.call!.type}
              source={OpenStorySource.STORY_RIBBON}
            />
            <HallAvatarName hallId={room.id} />
          </StoryRibbonItemWrapper>
        )
      })}

      {nonClientRibbons.map(({ peerId, peerType }) => {
        const isUser = peerType === 'USER'
        const isHall = peerType === 'CHANNEL' || peerType === 'GROUP'
        return (
          <StoryRibbonItemWrapper key={`story-${peerId + peerType}`}>
            {isUser ? (
              <>
                <UserAvatar
                  userId={peerId}
                  size={AvatarSize.LG}
                  withStory
                  isStoryRibbon
                  source={OpenStorySource.STORY_RIBBON}
                />
                <UserAvatarName userId={peerId} />
              </>
            ) : isHall ? (
              <>
                <HallAvatar
                  roomId={peerId}
                  size={AvatarSize.LG}
                  withStory
                  isInStoryRibbon
                  source={OpenStorySource.STORY_RIBBON}
                />
                <HallAvatarName hallId={peerId} />
              </>
            ) : null}
          </StoryRibbonItemWrapper>
        )
      })}
    </StoryRibbonRoot>
  )
}

const StoryRibbonRoot = styled('div')`
  display: flex;
  gap: 8px;
  direction: ltr;
  padding: 0 8px;
  overflow: auto hidden;
  background-color: ${({ theme }) => theme.palette.neutral000};
  transition: height ${STORY_RIBBON_TRANSITION_MS}ms ease;
  height: ${STORY_RIBBON_HEIGHT_CSS_VAR};
  align-items: center;
`

const StoryRibbonItemWrapper = styled('div')`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  width: 68px;
  align-items: center;
  gap: 4px;
`

function UserAvatarName({ userId }: { userId: string }) {
  const { user } = useUser(userId)
  return <AvatarName name={user ? getUserDisplayName(user) : undefined} />
}

function HallAvatarName({ hallId }: { hallId: string }) {
  const { entity: hall } = useHall(hallId)
  return <AvatarName name={hall?.name} />
}

function AvatarName({ name }: { name?: string }) {
  return name ? (
    <Typography
      variant="caption2"
      color="neutral800"
      noWrap
      maxWidth="100%"
      dir="auto"
      sx={{
        unicodeBidi: 'plaintext',
      }}
    >
      {name}
    </Typography>
  ) : (
    <Skeleton variant="rounded" width="80%" height={8} />
  )
}
